import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { supabase } from "../supabaseClient";

interface SelectedIngredient {
  value: string;
  label: string;
}

interface IngredientsContextType {
  selectedIngredients: SelectedIngredient[];
  setSelectedIngredients: React.Dispatch<
    React.SetStateAction<SelectedIngredient[]>
  >;
}

const IngredientsContext = createContext<IngredientsContextType | undefined>(
  undefined
);

export const IngredientsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedIngredients, setSelectedIngredients] = useState<
    SelectedIngredient[]
  >([]);

  // 🔁 Lataa tallennetut raaka-aineet kirjautuneelle tai vierailijalle
  useEffect(() => {
    const fetchInitialIngredients = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const user = session?.user;

      if (user) {
        const { data, error } = await supabase
          .from("user_ingredients")
          .select("label, value")
          .eq("user_id", user.id);

        if (data && Array.isArray(data)) {
          setSelectedIngredients(data);
          console.log("✅ Supabase ingredients:", data);
        } else if (error) {
          console.error("❌ Virhe haettaessa Supabasesta:", error.message);
        }
      } else {
        const guestData = sessionStorage.getItem("ingredients");
        if (guestData) {
          const parsed = JSON.parse(guestData);
          setSelectedIngredients(parsed);
          console.log("📦 Ladattiin sessionStoragesta:", parsed);
        }
      }
    };

    fetchInitialIngredients();
  }, []);

  // 💾 Tallennus sessionStorageen vierailijoille
  useEffect(() => {
    const storeForGuest = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session?.user) {
        sessionStorage.setItem(
          "ingredients",
          JSON.stringify(selectedIngredients)
        );
        console.log("💾 Tallennettiin sessionStorageen:", selectedIngredients);
      }
    };

    storeForGuest();
  }, [selectedIngredients]);

  // 🔄 Tyhjennä kirjautuneen tiedot uloskirjautuessa ja lataa vierailijan sessionStorage
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_OUT") {
          setSelectedIngredients([]);
          const guestData = sessionStorage.getItem("ingredients");
          if (guestData) {
            const parsed = JSON.parse(guestData);
            setSelectedIngredients(parsed);
            console.log("🔁 Palautettiin vierailijan raaka-aineet:", parsed);
          }
        }
      }
    );

    return () => {
      listener.subscription?.unsubscribe();
    };
  }, []);

  return (
    <IngredientsContext.Provider
      value={{ selectedIngredients, setSelectedIngredients }}
    >
      {children}
    </IngredientsContext.Provider>
  );
};

export const useIngredients = (): IngredientsContextType => {
  const context = useContext(IngredientsContext);
  if (!context) {
    throw new Error(
      "❌ useIngredients must be used within an IngredientsProvider"
    );
  }
  return context;
};
